import React from "react";
import { View, Text, StyleSheet } from "react-native";
import Logo from "../../general/logos/Logo";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../common/general/CustomText/CustomText";
import { DatePickerInput } from "react-native-paper-dates";
import DropdownSelect from "react-native-input-select";
import { Checked, UnChecked } from "../../svgs/common";
import { useAppConfig } from "../../../AppConfigProvider";
import { ActivityIndicator } from "react-native-paper";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { z } from "zod";
import { calculateAge } from "../../../containers/auth/SignupFormContainer/SignupFormContainer";

const INPUT_HEIGHT = 40;

export const signupSchema = z
  .object({
    name: z
      .string()
      .min(1, "First name is required")
      .refine((value) => !value.includes(" "), "For security, please only provide your first name."),
    email: z.string().min(1, "Email is required").email("Please enter a valid email"),
    password: z
      .string()
      .min(10, "Password must be at least 10 characters long")
      .refine(
        (value) => /[A-Z]/.test(value) && /[\d!@#$%^&*()_+{}":;'<>?,./\\-]/.test(value),
        "Password must contain at least 1 uppercase letter, and 1 number or special character"
      ),
    confirmPassword: z.string().min(1, "Confirm password is required"),
    birthday: z
      .date({
        required_error: "Birthday is required",
        invalid_type_error: "That's not a date!",
      })
      .nullable()
      .refine((value) => {
        if (!value) return false;
        const age = calculateAge(value);
        return age >= 14;
      }, "You must be 14 years old or older to sign up."),
    gender: z.string().min(1, "Please select your gender"),
    hasAgreedToTerms: z.boolean().refine((value) => value === true, "You must agree to the Terms of Service"),
    dailyEmailOptIn: z.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type SignupFormData = z.infer<typeof signupSchema>;

interface SignupFormProps {
  control: Control<SignupFormData>;
  errors: FieldErrors<SignupFormData>;
  logo: string;
  errorMessage: string;
  isLoading: boolean;
  onTermsPress: () => void;
  onSubmit: () => void;
  onSignInPress: () => void;
  passwordHidden: boolean;
  confirmPasswordHidden: boolean;
  onPasswordToggle: () => void;
  onConfirmPasswordToggle: () => void;
}

function SignupForm({
  control,
  errors,
  logo,
  errorMessage,
  isLoading,
  onTermsPress,
  onSubmit,
  onSignInPress,
  passwordHidden,
  confirmPasswordHidden,
  onPasswordToggle,
  onConfirmPasswordToggle,
}: SignupFormProps) {
  const { primaryColor } = useAppConfig();

  return (
    <View style={styles.container}>
      <View accessible={true} accessibilityRole="header">
        <Logo uri={logo} />
        <CustomSpacing type="vertical" size="m" />
        <CustomText text="Create your free account" textType="display" size="xs" bold />
      </View>
      <CustomSpacing type="vertical" size="m" />

      <CustomText text="First Name" size="s" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            placeholder="First Name..."
            style={[errors?.name && styles.textInputError, styles.textInput]}
            aria-label="First name text input"
          />
        )}
      />
      {errors?.name ? (
        <>
          <CustomSpacing type="vertical" size="xs" />
          <CustomText
            text={errors.name.message || "For security, please only provide your first name."}
            size="xs"
            style={styles.errorText}
          />
        </>
      ) : (
        <CustomSpacing type="vertical" size="m" />
      )}
      <CustomSpacing type="vertical" size="s" />

      <CustomText text="Email Address" size="s" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        control={control}
        name="email"
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            placeholder="Email..."
            style={[errors?.email && styles.textInputError, styles.textInput]}
            aria-label="Email text input"
          />
        )}
      />
      {errors?.email ? (
        <>
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={errors.email.message || "Please enter a valid email."} size="xs" style={styles.errorText} />
        </>
      ) : (
        <CustomSpacing type="vertical" size="m" />
      )}
      <CustomSpacing type="vertical" size="s" />

      <CustomText text="Password" size="s" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        control={control}
        name="password"
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            value={value}
            placeholder="Password..."
            style={[errors?.password && styles.textInputError, styles.textInput]}
            onChangeText={onChange}
            onBlur={onBlur}
            secureTextEntry={passwordHidden}
            onToggleSecureEntry={onPasswordToggle}
            aria-label="Password text input"
          />
        )}
      />
      {errors?.password ? (
        <>
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={errors.password.message} size="xs" style={styles.errorText} />
        </>
      ) : (
        <CustomSpacing type="vertical" size="m" />
      )}
      <CustomSpacing type="vertical" size="s" />

      <CustomText text="Confirm Password" size="s" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        control={control}
        name="confirmPassword"
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            value={value}
            placeholder="Confirm Password..."
            style={[errors?.confirmPassword && styles.textInputError, styles.textInput]}
            onChangeText={onChange}
            onBlur={onBlur}
            secureTextEntry={confirmPasswordHidden}
            onToggleSecureEntry={onConfirmPasswordToggle}
            aria-label="Second password text input"
          />
        )}
      />
      <View aria-live={errors.confirmPassword ? "assertive" : "off"}>
        {errors.confirmPassword ? (
          <>
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              role="alert"
              text={errors.confirmPassword.message || "Passwords do not match."}
              size="xs"
              style={styles.errorText}
            />
          </>
        ) : (
          <CustomSpacing type="vertical" size="m" />
        )}
      </View>
      <CustomSpacing type="vertical" size="s" />

      <CustomText text="Birthday" size="s" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        control={control}
        name="birthday"
        render={({ field: { onChange, onBlur, value } }) => (
          <DatePickerInput
            locale="en"
            value={value}
            onChange={(date) => {
              onChange(date);
              onBlur();
            }}
            inputMode="start"
            mode="outlined"
            outlineColor={"#D1D5DB"}
            activeOutlineColor={"blue"}
            theme={{
              colors: {
                primary: "#000000",
              },
              roundness: 8,
            }}
            style={styles.datePicker}
            aria-label="Birthday date picker"
          />
        )}
      />
      {errors?.birthday ? (
        <>
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={errors.birthday.message} size="xs" style={styles.errorText} />
        </>
      ) : (
        <CustomSpacing type="vertical" size="m" />
      )}
      <CustomSpacing type="vertical" size="s" />

      <CustomText text="Gender" size="s" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        control={control}
        name="gender"
        render={({ field: { onChange, onBlur, value } }) => (
          <DropdownSelect
            placeholder="Select an option..."
            options={[
              { label: "Male", value: "Male" },
              { label: "Female", value: "Female" },
            ]}
            dropdownContainerStyle={styles.genderDropdownContainer}
            dropdownStyle={styles.genderDropdown}
            selectedValue={value as string}
            optionLabel={"label"}
            optionValue={"value"}
            onValueChange={(selectedValue) => {
              onChange(selectedValue);
              onBlur();
            }}
            primaryColor={"blue"}
            labelStyle={styles.genderLabel}
            listHeaderComponent={
              <View style={styles.genderDropdownLabel}>
                <CustomText text="Select Your Gender" weight="bold" />
              </View>
            }
            aria-label="Gender dropdown menu"
          />
        )}
      />
      {errors?.gender ? (
        <>
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={errors.gender.message} size="xs" style={styles.errorText} />
        </>
      ) : (
        <CustomSpacing type="vertical" size="m" />
      )}
      <CustomSpacing type="vertical" size="s" />

      <View style={styles.termsContainer}>
        <Controller
          control={control}
          name="dailyEmailOptIn"
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomButton
              styleType="transparent"
              role="checkbox"
              aria-checked={value}
              aria-label="Daily email opt in/opt out"
              style={styles.termsButton}
              leftIcon={value ? <Checked /> : <UnChecked />}
              onPress={() => {
                onChange(!value);
                onBlur();
              }}
            />
          )}
        />
        <Text style={styles.termsText}>Receive daily plan email</Text>
      </View>

      <CustomSpacing type="vertical" size="m" />

      <View style={styles.termsContainer}>
        <Controller
          control={control}
          name="hasAgreedToTerms"
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomButton
              styleType="transparent"
              role="checkbox"
              aria-checked={value}
              aria-label="Agree to terms"
              style={styles.termsButton}
              leftIcon={value ? <Checked /> : <UnChecked />}
              onPress={() => {
                onChange(!value);
                onBlur();
              }}
            />
          )}
        />
        <Text style={styles.termsText}>
          I agree to the{" "}
          <Text role="link" style={styles.termsLink} onPress={onTermsPress}>
            Terms of Service and Privacy Policy.
          </Text>
        </Text>
      </View>
      {errors.hasAgreedToTerms && (
        <>
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={errors.hasAgreedToTerms.message} size="xs" style={styles.errorText} />
        </>
      )}

      <View aria-live={errorMessage ? "assertive" : "off"}>
        {errorMessage ? (
          <>
            <CustomSpacing type="vertical" size="s" />
            <CustomText
              role="alert"
              text={errorMessage}
              size="s"
              style={styles.errorMessage}
              useTranslationText={false}
            />
            <CustomSpacing type="vertical" size="s" />
          </>
        ) : (
          <CustomSpacing type="vertical" size="xl" />
        )}
      </View>
      {isLoading ? (
        <View style={styles.loader}>
          <ActivityIndicator color={primaryColor} />
        </View>
      ) : (
        <CustomButton
          disabled={isLoading}
          text="Sign up"
          onPress={onSubmit}
          size="m"
          styleType="primary"
          style={styles.signupButton}
        />
      )}

      <CustomSpacing type="vertical" size="l" />

      <View style={styles.footer}>
        <CustomText text="Already a member?" size="s" style={styles.footerText} />
        <CustomButton
          accessibilityHint="Press to sign in"
          text="Sign In"
          styleType="transparent"
          size="s"
          onPress={onSignInPress}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: "auto",
    width: "100%",
    maxWidth: 600,
  },
  textInput: {
    backgroundColor: "#F8F8F8",
    borderColor: "#DEDEDE",
    height: INPUT_HEIGHT,
  },
  textInputError: {
    borderColor: "red",
    padding: 9,
  },
  errorText: {
    color: "red",
  },
  errorMessage: {
    color: "red",
    textAlign: "center",
  },
  datePicker: {
    borderWidth: 0,
    paddingVertical: 10,
    backgroundColor: "#F8F8F8",
    borderColor: "#000000",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
    height: INPUT_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
  },
  genderDropdown: {
    backgroundColor: "#F8F8F8",
    paddingHorizontal: 10,
    borderColor: "#D1D5DB",
    paddingBottom: 10,
    paddingTop: 10,
    minHeight: INPUT_HEIGHT,
    justifyContent: "center",
  },
  genderDropdownContainer: {
    marginBottom: 0,
  },
  genderDropdownLabel: {
    marginLeft: 10,
  },
  genderLabel: {
    color: "#000000",
    fontSize: 15,
    fontWeight: "500",
  },
  termsContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  termsButton: {
    padding: 0,
    marginRight: 10,
  },
  termsText: {
    fontSize: 14,
  },
  termsLink: {
    color: "#3E68FE",
    textDecorationLine: "underline",
  },
  signupButton: {
    justifyContent: "center",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  footerText: {
    color: "#6B7280",
  },
  loader: {
    justifyContent: "center",
    alignItems: "center",
  },
});

export default SignupForm;
